import {
  ApiResponse,
  PaginatedResponse,
  Fixture,
  CreateFixtureForm,
  MatchReport,
} from '@/types';
import api from './client';

// Fixtures API
export const fixturesAPI = {
  list: async (params?: { from?: string; to?: string; status?: Fixture['status']; page?: number; limit?: number }): Promise<PaginatedResponse<Fixture>> => {
    const sp = new URLSearchParams();
    if (params?.from) sp.append('from', params.from);
    if (params?.to) sp.append('to', params.to);
    if (params?.status) sp.append('status', params.status);
    if (params?.page) sp.append('page', String(params.page));
    if (params?.limit) sp.append('limit', String(params.limit));
    const res = await api.get(`/fixtures?${sp.toString()}`);
    return res.data;
  },
  getById: async (id: string): Promise<ApiResponse<Fixture>> => {
    const res = await api.get(`/fixtures/${id}`);
    return res.data;
  },
  create: async (payload: CreateFixtureForm): Promise<ApiResponse<Fixture>> => {
    const res = await api.post('/fixtures', payload);
    return res.data;
  },
  update: async (id: string, payload: Partial<CreateFixtureForm>): Promise<ApiResponse<Fixture>> => {
    const res = await api.patch(`/fixtures/${id}`, payload);
    return res.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/fixtures/${id}`);
  },
  getSquad: async (id: string): Promise<ApiResponse<Array<{ player_id: string; role: 'starter'|'bench'|'reserve'; shirt_number?: number; name: string; position: string }>>> => {
    const res = await api.get(`/fixtures/${id}/squad`);
    return res.data;
  },
  putSquad: async (id: string, members: Array<{ player_id: string; role: 'starter'|'bench'|'reserve'; shirt_number?: number }>): Promise<void> => {
    await api.put(`/fixtures/${id}/squad`, { members });
  },
  getEligibility: async (id: string): Promise<ApiResponse<{ eligible: Array<{id: string; name: string}>; doubtful: Array<{id: string; name: string; reason: string}>; ineligible: Array<{id: string; name: string; reason: string}> }>> => {
    const res = await api.get(`/fixtures/${id}/eligibility`);
    return res.data;
  },
};

// Post-match review API
export const postMatchAPI = {
  getReport: async (fixtureId: string): Promise<ApiResponse<MatchReport>> => {
    const res = await api.get(`/matches/${fixtureId}/report`);
    return res.data;
  },
  upsertReport: async (fixtureId: string, payload: Partial<{ home_score?: number; away_score?: number; shots_home?: number; shots_away?: number; shots_on_target_home?: number; shots_on_target_away?: number; corners_home?: number; corners_away?: number; fouls_home?: number; fouls_away?: number; yellows_home?: number; yellows_away?: number; reds_home?: number; reds_away?: number; possession_home?: number; possession_away?: number; xg_home?: number; xg_away?: number; summary?: string; result?: Record<string, unknown>; stats?: Record<string, unknown> }>): Promise<ApiResponse<MatchReport>> => {
    const res = await api.patch(`/matches/${fixtureId}/report`, payload);
    return res.data;
  },
  listEvents: async (fixtureId: string): Promise<ApiResponse<Array<{ id: string; minute?: number; type: string; team: 'own'|'opp'; playerId?: string; details?: Record<string, unknown>; createdAt: string }>>> => {
    const res = await api.get(`/matches/${fixtureId}/events`);
    return res.data;
  },
  bulkUpsertEvents: async (fixtureId: string, events: Array<{ minute?: number; type: string; team: 'own'|'opp'; player_id?: string; details?: Record<string, unknown> }>): Promise<void> => {
    await api.post(`/matches/${fixtureId}/events`, { events });
  },
  putRatings: async (fixtureId: string, ratings: Array<{ player_id: string; rating?: number; notes?: string }>): Promise<void> => {
    await api.post(`/matches/${fixtureId}/report/ratings`, { ratings });
  },
  addAttachment: async (fixtureId: string, payload: { url: string; kind: 'video'|'doc'|'image'; description?: string }): Promise<ApiResponse<{ id: string; createdAt: string }>> => {
    const res = await api.post(`/matches/${fixtureId}/report/attachments`, payload);
    return res.data;
  },
  addActionItem: async (fixtureId: string, payload: { title: string; owner_user_id?: string; due_date?: string }): Promise<ApiResponse<{ id: string; createdAt: string }>> => {
    const res = await api.post(`/matches/${fixtureId}/report/action_items`, payload);
    return res.data;
  },
  export: async (fixtureId: string): Promise<ApiResponse<MatchReport>> => {
    const res = await api.get(`/matches/${fixtureId}/report/export`);
    return res.data;
  },
  listNotes: async (fixtureId: string): Promise<ApiResponse<Array<{ id: string; matchReportId: string; authorUserId?: string; body: string; createdAt: string }>>> => {
    const res = await api.get(`/matches/${fixtureId}/report/notes`);
    return res.data;
  },
  addNote: async (fixtureId: string, body: string): Promise<ApiResponse<{ id: string; matchReportId: string; authorUserId?: string; body: string; createdAt: string }>> => {
    const res = await api.post(`/matches/${fixtureId}/report/notes`, { body });
    return res.data;
  },
};

export const teamsAPI = {
  list: async (): Promise<ApiResponse<Array<{ id: string; name: string }>>> => {
    const res = await api.get('/teams');
    return res.data;
  },
};
